import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import { Typography, Box, CircularProgress } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import clsx from "clsx";
import Navbar from "components/Navbar";
import DarkLayout from "components/Shared/DarkLayout";
import { PATHS } from "util/appConstants";
import { getRoleById, selectRole, selectRolesStatus } from "redux/slices/rolesSlice";
import { selectPermissions, selectPermissionsStatus, getAllPermissions } from "redux/slices/permissionsSlice";
import PermissionsManager from "components/Permissions/PermissionsManager";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _infoheading: {
    color: "#F5F5F5",
    font: "normal normal medium 22px/32px Roboto",
    marginTop: "44px",
  },
  _head: {
    color: "#F5F5F5",
    opacity: "0.6",
    font: "normal normal normal 12px/24px Roboto",
    marginRight: "32px",
  },
  _basicdetailhead: {
    display: "flex",
    marginTop: "24px",
    flexWrap: "wrap",
    gap: "32px",
  },
  _basicdetail: {
    display: "flex",
  },
  _detail: {
    color: "#F5F5F5",
    marginRight: "32px",
    font: "normal normal normal 18px/24px Roboto",
  },
  _name: {
    letterSpacing: "0px",
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
});

const RoleDetail = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  const roleLoading = useSelector(selectRolesStatus);
  const role = useSelector(selectRole);
  const permissions = useSelector(selectPermissions);
  const permissionsLoading = useSelector(selectPermissionsStatus);

  useEffect(() => {
    if (id && !roleLoading) {
      dispatch(getRoleById(id));
    }
    dispatch(getAllPermissions());
  }, [dispatch, id]);

  const editHandler = () => {
    history.push(PATHS.admin.roleEdit.replace(":id", id));
  };

  const permissionsValueMap = useMemo(() => {
    const map = {};
    if (!permissions.length) return map;

    permissions.forEach((perm) => {
      map[perm.id] = false;
    });

    const rolePerms =
      role?.Permissions ||
      role?.RoleHasPermissions ||
      role?.roleHasPermissions ||
      [];

    rolePerms.forEach((perm) => {
      const permId = perm.permission_id !== undefined ? perm.permission_id : perm.id;
      const permValue = perm.value !== undefined ? Boolean(perm.value) : true;
      if (permId !== undefined && permId !== null) {
        map[permId] = permValue;
      }
    });

    return map;
  }, [permissions, role]);

  return (
    <>
      <Navbar />
      <DarkLayout doublebar loading={roleLoading || !role || permissionsLoading}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {role?.name}
          </Typography>
          <Box className={classes._buttonbox} component="div">
            <EditIcon onClick={editHandler} className={classes._edit} />
            <Typography component="span" className={clsx(classes._edittext, "edittag")}>
              {t("Edit")}
            </Typography>
          </Box>
        </div>

        <Typography className={classes._infoheading} variant="h5">
          {t("Basic Data")}
        </Typography>
        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={classes._head} variant="h6">
              {t("Id")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {role?.id}
            </Typography>
          </div>
          <div>
            <Typography className={classes._head} variant="h6">
              {t("Name")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {role?.name}
            </Typography>
          </div>
        </div>

        <Typography className={classes._infoheading} variant="h5">
          {t("Permissions")}
        </Typography>

        {!permissions.length ? (
          <CircularProgress />
        ) : (
          <Box mt={2}>
            <PermissionsManager
              permissions={permissions}
              value={permissionsValueMap}
              disabled={true}
              darkMode={true}
            />
          </Box>
        )}
      </DarkLayout>
    </>
  );
};

export default RoleDetail;
